import { BLOCKING_FEATURE_KINDS, ROOM_OPENING_LABELS, } from '../types/room-shape.js';
const EPS = 1e-9;
const round = (n, places = 3) => Math.round(n * 10 ** places) / 10 ** places;
/** Straight-line and curved walls flatten to this many pieces per half circle. */
const ARC_STEPS_PER_HALF_TURN = 16;
// ─── Walls ──────────────────────────────────────────────────────────
/** The wall starting at `i`: its two ends and its bulge. */
export function wallAt(outline, i) {
    const a = outline[i];
    const b = outline[(i + 1) % outline.length];
    return { a, b, bulge: a.bulge ?? 0 };
}
/** The circle a bulged wall runs along; null for a straight one. */
export function arcOf(a, b, bulge) {
    if (Math.abs(bulge) < EPS)
        return null;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const chord = Math.hypot(dx, dy);
    if (chord < EPS)
        return null;
    const theta = 4 * Math.atan(Math.abs(bulge));
    const radius = chord / (2 * Math.sin(theta / 2));
    const sign = Math.sign(bulge);
    // Unit normal to the left of travel; the arc's peak lies that way for a
    // positive bulge, and the centre on the other side of the chord.
    const nx = -dy / chord;
    const ny = dx / chord;
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    // The arc's height over the chord (its sagitta) is half the chord × |bulge|.
    const apexOffset = sign * (chord / 2) * Math.abs(bulge);
    const apex = { x: mid.x + nx * apexOffset, y: mid.y + ny * apexOffset };
    const center = {
        x: apex.x - nx * sign * radius,
        y: apex.y - ny * sign * radius,
    };
    const startAngle = Math.atan2(a.y - center.y, a.x - center.x);
    // Choose the sweep direction that passes through the apex.
    const tryDir = (dir) => {
        const m = startAngle + (dir * theta) / 2;
        return Math.hypot(center.x + radius * Math.cos(m) - apex.x, center.y + radius * Math.sin(m) - apex.y);
    };
    const sweep = tryDir(1) <= tryDir(-1) ? theta : -theta;
    return { center, radius, startAngle, sweep };
}
/** Points along a wall, excluding its end (the next wall starts there). */
export function wallPoints(a, b, bulge) {
    const arc = arcOf(a, b, bulge);
    if (!arc)
        return [{ x: a.x, y: a.y }];
    const steps = Math.max(2, Math.ceil((Math.abs(arc.sweep) / Math.PI) * ARC_STEPS_PER_HALF_TURN));
    const pts = [];
    for (let s = 0; s < steps; s++) {
        const ang = arc.startAngle + (arc.sweep * s) / steps;
        pts.push({
            x: arc.center.x + arc.radius * Math.cos(ang),
            y: arc.center.y + arc.radius * Math.sin(ang),
        });
    }
    pts[0] = { x: a.x, y: a.y };
    return pts;
}
/** Length of a wall, along its curve. */
export function wallLength(a, b, bulge = 0) {
    const arc = arcOf(a, b, bulge);
    if (!arc)
        return Math.hypot(b.x - a.x, b.y - a.y);
    return Math.abs(arc.sweep) * arc.radius;
}
/** A point `t` (0–1) of the way along a wall, and the wall's direction there. */
export function pointOnWall(a, b, bulge, t) {
    const arc = arcOf(a, b, bulge);
    if (!arc) {
        return {
            point: { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t },
            angle: Math.atan2(b.y - a.y, b.x - a.x),
        };
    }
    const ang = arc.startAngle + arc.sweep * t;
    return {
        point: {
            x: arc.center.x + arc.radius * Math.cos(ang),
            y: arc.center.y + arc.radius * Math.sin(ang),
        },
        angle: ang + (Math.sign(arc.sweep) * Math.PI) / 2,
    };
}
// ─── Outline ────────────────────────────────────────────────────────
/** The outline as a plain polygon, curves broken into short straight pieces. */
export function flattenOutline(outline) {
    const pts = [];
    outline.forEach((_, i) => {
        const { a, b, bulge } = wallAt(outline, i);
        pts.push(...wallPoints(a, b, bulge));
    });
    return pts;
}
export function polygonSignedArea(points) {
    let sum = 0;
    for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        sum += a.x * b.y - b.x * a.y;
    }
    return sum / 2;
}
export function polygonArea(points) {
    return Math.abs(polygonSignedArea(points));
}
export function pointsBounds(points) {
    if (points.length === 0)
        return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const p of points) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
    }
    return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
}
export function perimeterLength(outline) {
    return outline.reduce((sum, _, i) => {
        const { a, b, bulge } = wallAt(outline, i);
        return sum + wallLength(a, b, bulge);
    }, 0);
}
/** Ray casting; points on the boundary count as inside. */
export function pointInPolygon(p, poly) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const a = poly[i];
        const b = poly[j];
        if (a.y > p.y !== b.y > p.y) {
            const x = ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
            if (p.x < x)
                inside = !inside;
        }
    }
    return inside;
}
function segmentsCross(p1, p2, p3, p4) {
    const d = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    const d1 = d(p3, p4, p1);
    const d2 = d(p3, p4, p2);
    const d3 = d(p1, p2, p3);
    const d4 = d(p1, p2, p4);
    return (((d1 > EPS && d2 < -EPS) || (d1 < -EPS && d2 > EPS)) &&
        ((d3 > EPS && d4 < -EPS) || (d3 < -EPS && d4 > EPS)));
}
const TOUCH_FT = 1e-6;
/** Distance from `p` to the segment a–b. */
function distanceToSegment(p, a, b) {
    const t = projectOntoWall(a, b, p);
    return Math.hypot(a.x + (b.x - a.x) * t - p.x, a.y + (b.y - a.y) * t - p.y);
}
/** The segments cross, or one ends on the other (touching, or lying along it). */
function segmentsMeet(p1, p2, p3, p4) {
    return (segmentsCross(p1, p2, p3, p4) ||
        distanceToSegment(p1, p3, p4) < TOUCH_FT ||
        distanceToSegment(p2, p3, p4) < TOUCH_FT ||
        distanceToSegment(p3, p1, p2) < TOUCH_FT ||
        distanceToSegment(p4, p1, p2) < TOUCH_FT);
}
/**
 * True when two walls that are not neighbours cross, touch or run along each
 * other — a pinched outline is no more a room than a crossed one.
 */
export function selfIntersects(outline) {
    const pts = flattenOutline(outline);
    const n = pts.length;
    for (let i = 0; i < n; i++) {
        for (let j = i + 2; j < n; j++) {
            if (i === 0 && j === n - 1)
                continue; // neighbours through the closing wall
            if (segmentsMeet(pts[i], pts[(i + 1) % n], pts[j], pts[(j + 1) % n]))
                return true;
        }
    }
    return false;
}
// ─── Measurements ───────────────────────────────────────────────────
/** Whether a point is on a feature, its rotation and shape included. */
export function pointInFeature(p, f) {
    const cx = f.x + f.widthFt / 2;
    const cy = f.y + f.heightFt / 2;
    const rad = (-(f.rotation ?? 0) * Math.PI) / 180;
    const dx = p.x - cx;
    const dy = p.y - cy;
    const lx = dx * Math.cos(rad) - dy * Math.sin(rad);
    const ly = dx * Math.sin(rad) + dy * Math.cos(rad);
    const hw = f.widthFt / 2;
    const hh = f.heightFt / 2;
    if (hw <= 0 || hh <= 0)
        return false;
    if (f.shape === 'CIRCLE')
        return (lx / hw) ** 2 + (ly / hh) ** 2 <= 1;
    return Math.abs(lx) <= hw && Math.abs(ly) <= hh;
}
/** Most grid cells the blocked-area count will look at, whatever the features' size. */
const MAX_BLOCKED_CELLS = 250_000;
/**
 * Floor covered by the blocking features, counted once where they overlap and
 * only where it is inside the room. Measured on a grid of small cells (a
 * quarter foot, coarser only for very large features) — exact for features
 * set on the grid, and within a fraction of a square foot otherwise.
 */
export function blockedFloorArea(shape) {
    const blocking = shape.features.filter((f) => BLOCKING_FEATURE_KINDS.includes(f.kind));
    if (blocking.length === 0)
        return 0;
    const room = flattenOutline(shape.outline);
    if (room.length < 3)
        return 0;
    const boxes = blocking.map((f) => {
        // A turned feature's box is bounded by its diagonal.
        const r = f.rotation ? Math.hypot(f.widthFt, f.heightFt) / 2 : 0;
        const cx = f.x + f.widthFt / 2;
        const cy = f.y + f.heightFt / 2;
        return r
            ? { minX: cx - r, minY: cy - r, maxX: cx + r, maxY: cy + r }
            : { minX: f.x, minY: f.y, maxX: f.x + f.widthFt, maxY: f.y + f.heightFt };
    });
    const cellsAt = (c) => boxes.reduce((n, b) => n + Math.ceil((b.maxX - b.minX) / c) * Math.ceil((b.maxY - b.minY) / c), 0);
    let cell = 0.25;
    while (cellsAt(cell) > MAX_BLOCKED_CELLS)
        cell *= 2;
    const covered = new Set();
    boxes.forEach((b) => {
        for (let i = Math.floor(b.minX / cell); i * cell < b.maxX; i++) {
            for (let j = Math.floor(b.minY / cell); j * cell < b.maxY; j++) {
                const key = `${i},${j}`;
                if (covered.has(key))
                    continue;
                const centre = { x: (i + 0.5) * cell, y: (j + 0.5) * cell };
                if (blocking.some((f) => pointInFeature(centre, f)) && pointInPolygon(centre, room)) {
                    covered.add(key);
                }
            }
        }
    });
    return covered.size * cell * cell;
}
export function measureRoomShape(shape) {
    const flat = flattenOutline(shape.outline);
    const areaSqFt = polygonArea(flat);
    const blocked = blockedFloorArea(shape);
    const box = pointsBounds(flat);
    return {
        areaSqFt: round(areaSqFt, 1),
        usableAreaSqFt: round(Math.max(0, areaSqFt - blocked), 1),
        perimeterFt: round(perimeterLength(shape.outline), 1),
        widthFt: round(box.width, 2),
        heightFt: round(box.height, 2),
        walls: shape.outline.length,
    };
}
// ─── Validation ─────────────────────────────────────────────────────
/** How far an opening may overhang its wall's end before it is a problem (rounding). */
const OPENING_FIT_FT = 0.01;
function openingFits(wallLenFt, position, widthFt) {
    const centre = position * wallLenFt;
    return (centre - widthFt / 2 >= -OPENING_FIT_FT && centre + widthFt / 2 <= wallLenFt + OPENING_FIT_FT);
}
/**
 * The nearest position (0–1) along a wall `wallLenFt` long where an opening
 * `widthFt` wide fits whole — what sliding a door toward a corner stops at.
 */
export function clampOpeningPosition(wallLenFt, widthFt, position) {
    if (wallLenFt <= 0)
        return 0.5;
    const half = Math.min(widthFt, wallLenFt) / 2 / wallLenFt;
    return Math.min(1 - half, Math.max(half, position));
}
/** What is wrong with a shape, in words a producer can act on; empty when fine. */
export function roomShapeProblems(shape) {
    const problems = [];
    const flat = flattenOutline(shape.outline);
    if (shape.outline.length < 2 || flat.length < 3) {
        problems.push('A room needs at least three corners.');
        return problems;
    }
    if (polygonArea(flat) < 1)
        problems.push('The outline encloses no floor.');
    for (let i = 0; i < shape.outline.length; i++) {
        const { a, b } = wallAt(shape.outline, i);
        if (Math.hypot(b.x - a.x, b.y - a.y) < 0.1) {
            problems.push(`Wall ${i + 1} has no length — two corners sit on top of each other.`);
        }
    }
    if (selfIntersects(shape.outline))
        problems.push('Two walls cross each other.');
    shape.openings.forEach((o) => {
        const name = o.label || ROOM_OPENING_LABELS[o.kind];
        if (o.segment < 0 || o.segment >= shape.outline.length) {
            problems.push(`${name} is on a wall that no longer exists.`);
            return;
        }
        const { a, b, bulge } = wallAt(shape.outline, o.segment);
        const len = wallLength(a, b, bulge);
        const wall = `wall ${o.segment + 1}`;
        if (o.widthFt > len + OPENING_FIT_FT) {
            problems.push(`${name} (${o.widthFt} ft) is wider than ${wall} (${round(len, 1)} ft).`);
        }
        else if (!openingFits(len, o.position, o.widthFt)) {
            problems.push(`${name} runs past the end of ${wall} — slide it along the wall.`);
        }
    });
    return problems;
}
// ─── Building shapes ────────────────────────────────────────────────
export function emptyRoomShape(outline = []) {
    return { version: 1, outline, features: [], openings: [] };
}
/** The shape a room already has: its drawn perimeter, or its rectangle. */
export function roomShapeFromRoom(room) {
    const stored = room.shape;
    if (stored && Array.isArray(stored.outline) && stored.outline.length >= 2) {
        return {
            version: 1,
            outline: stored.outline,
            features: stored.features ?? [],
            openings: stored.openings ?? [],
            ...(stored.wallHeightFt ? { wallHeightFt: stored.wallHeightFt } : {}),
            ...(stored.background ? { background: stored.background } : {}),
        };
    }
    if (room.perimeterPoints && room.perimeterPoints.length >= 3) {
        return emptyRoomShape(room.perimeterPoints.map((p) => ({ x: p.x, y: p.y })));
    }
    return shapeFromPreset('RECTANGLE', { widthFt: room.widthFt, heightFt: room.heightFt });
}
/** What each preset's two extra dimensions mean, for the form beside it. */
export const PRESET_INSET_LABELS = {
    RECTANGLE: null,
    L_SHAPE: ['Cut-out width', 'Cut-out depth'],
    U_SHAPE: ['Notch width', 'Notch depth'],
    T_SHAPE: ['Stem width', 'Top depth'],
    CUT_CORNERS: ['Corner size', ''],
    ROUNDED_END: null,
    CIRCLE: null,
};
const clampInset = (v, max, fallback) => Math.min(Math.max(v ?? fallback, 1), Math.max(1, max - 1));
/** A starting outline to adjust, from a few measurements. */
export function shapeFromPreset(preset, d) {
    const w = Math.max(d.widthFt, 2);
    const h = Math.max(d.heightFt, 2);
    const v = (x, y, bulge) => bulge ? { x, y, bulge } : { x, y };
    let outline;
    switch (preset) {
        case 'L_SHAPE': {
            const cw = clampInset(d.insetWidthFt, w, w / 2);
            const ch = clampInset(d.insetDepthFt, h, h / 2);
            outline = [v(0, 0), v(w - cw, 0), v(w - cw, ch), v(w, ch), v(w, h), v(0, h)];
            break;
        }
        case 'U_SHAPE': {
            const nw = clampInset(d.insetWidthFt, w, w / 3);
            const nd = clampInset(d.insetDepthFt, h, h / 2);
            const left = (w - nw) / 2;
            outline = [
                v(0, 0),
                v(left, 0),
                v(left, nd),
                v(left + nw, nd),
                v(left + nw, 0),
                v(w, 0),
                v(w, h),
                v(0, h),
            ];
            break;
        }
        case 'T_SHAPE': {
            const sw = clampInset(d.insetWidthFt, w, w / 3);
            const top = clampInset(d.insetDepthFt, h, h / 3);
            const left = (w - sw) / 2;
            outline = [
                v(0, 0),
                v(w, 0),
                v(w, top),
                v(left + sw, top),
                v(left + sw, h),
                v(left, h),
                v(left, top),
                v(0, top),
            ];
            break;
        }
        case 'CUT_CORNERS': {
            const c = clampInset(d.insetWidthFt, Math.min(w, h) / 2, Math.min(w, h) * 0.2);
            outline = [
                v(c, 0),
                v(w - c, 0),
                v(w, c),
                v(w, h - c),
                v(w - c, h),
                v(c, h),
                v(0, h - c),
                v(0, c),
            ];
            break;
        }
        case 'ROUNDED_END': {
            const straight = Math.max(w - h / 2, 1);
            // The wall down the right end bulges outward (to the right of travel).
            outline = [v(0, 0), v(straight, 0, -1), v(straight, h), v(0, h)];
            break;
        }
        case 'CIRCLE': {
            const r = w / 2;
            outline = [v(0, r, -1), v(w, r, -1)];
            break;
        }
        case 'RECTANGLE':
        default:
            outline = [v(0, 0), v(w, 0), v(w, h), v(0, h)];
    }
    return emptyRoomShape(outline);
}
/**
 * Walk the room: each wall's length, then how far to turn. Starts at the
 * top-left heading right. `gapFt` is how far the last wall ends from the
 * first corner — non-zero when a measurement is off, and worth saying.
 */
export function shapeFromWalls(walls) {
    const outline = [];
    let x = 0;
    let y = 0;
    let heading = 0;
    walls.forEach((w) => {
        outline.push({ x: round(x), y: round(y) });
        x += w.lengthFt * Math.cos((heading * Math.PI) / 180);
        y += w.lengthFt * Math.sin((heading * Math.PI) / 180);
        heading += w.turnDeg;
    });
    return { outline, gapFt: round(Math.hypot(x, y), 2) };
}
/** The walls of an outline as "length, then turn" — the inverse of shapeFromWalls. */
export function wallsFromOutline(outline) {
    const n = outline.length;
    return outline.map((_, i) => {
        const { a, b, bulge } = wallAt(outline, i);
        const next = wallAt(outline, (i + 1) % n);
        const h1 = Math.atan2(b.y - a.y, b.x - a.x);
        const h2 = Math.atan2(next.b.y - next.a.y, next.b.x - next.a.x);
        let turn = ((h2 - h1) * 180) / Math.PI;
        while (turn > 180)
            turn -= 360;
        while (turn <= -180)
            turn += 360;
        return { lengthFt: round(wallLength(a, b, bulge), 2), turnDeg: round(turn, 1) };
    });
}
/**
 * Move the shape so its outline starts at 0,0 — the canvas and the stored
 * width/height describe the box the room occupies. Features and the traced
 * drawing move with it.
 */
export function normalizeRoomShape(shape) {
    const box = pointsBounds(flattenOutline(shape.outline));
    const dx = box.minX;
    const dy = box.minY;
    if (Math.abs(dx) < EPS && Math.abs(dy) < EPS)
        return shape;
    return {
        ...shape,
        outline: shape.outline.map((p) => ({ ...p, x: round(p.x - dx), y: round(p.y - dy) })),
        features: shape.features.map((f) => ({ ...f, x: round(f.x - dx), y: round(f.y - dy) })),
        ...(shape.background
            ? {
                background: {
                    ...shape.background,
                    x: shape.background.x - dx,
                    y: shape.background.y - dy,
                },
            }
            : {}),
    };
}
/** What the rest of the app stores beside a shape: its polygon and its box. */
export function roomShapeDerived(shape) {
    const flat = flattenOutline(shape.outline).map((p) => ({ x: round(p.x, 2), y: round(p.y, 2) }));
    const box = pointsBounds(flat);
    return {
        perimeterPoints: flat,
        widthFt: Math.max(1, round(box.width, 2)),
        heightFt: Math.max(1, round(box.height, 2)),
    };
}
// ─── Editing ────────────────────────────────────────────────────────
/** Nearest point on a straight wall to `p`, as a 0–1 fraction along it. */
export function projectOntoWall(a, b, p) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    if (len2 < EPS)
        return 0;
    return Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
}
/**
 * Where `p` falls along a wall, as the 0–1 fraction **along the wall as it is
 * drawn** — along the curve for a curved one, which is how an opening's
 * position is read. `p` is taken to the point of the wall level with it
 * along the wall's own direction (its corner-to-corner line): a wall bulges
 * at most a half circle, so moving along that line only ever moves forward
 * along the curve, and the answer changes smoothly wherever the pointer
 * goes. Measuring an angle about the arc's centre, or taking the nearest
 * point, both jump from the middle of the wall to an end as the pointer
 * crosses the centre. For a point on the wall it is exact; past either end,
 * that end.
 */
export function projectOntoCurvedWall(a, b, bulge, p) {
    const u = projectOntoWall(a, b, p);
    if (!arcOf(a, b, bulge) || u <= 0 || u >= 1)
        return u;
    // How far along the chord the wall's point at `t` lies — rises with t.
    const along = (t) => projectOntoWall(a, b, pointOnWall(a, b, bulge, t).point);
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 40; i++) {
        const mid = (lo + hi) / 2;
        if (along(mid) < u)
            lo = mid;
        else
            hi = mid;
    }
    return (lo + hi) / 2;
}
/**
 * Put a corner in the middle of wall `i` (or at `at`). The two halves keep the
 * wall's openings, each on the half it sat on.
 */
export function insertShapeVertex(shape, i, at) {
    const { a, b, bulge } = wallAt(shape.outline, i);
    const t = at ? projectOntoWall(a, b, at) : 0.5;
    // A curved wall is split straight: two new bulges would be a guess.
    const point = at ?? pointOnWall(a, b, bulge, t).point;
    const outline = [...shape.outline];
    outline[i] = { x: a.x, y: a.y };
    outline.splice(i + 1, 0, { x: round(point.x), y: round(point.y) });
    const openings = shape.openings.map((o) => {
        if (o.segment > i)
            return { ...o, segment: o.segment + 1 };
        if (o.segment < i)
            return o;
        return o.position <= t
            ? { ...o, position: t > 0 ? o.position / t : 0 }
            : { ...o, segment: i + 1, position: t < 1 ? (o.position - t) / (1 - t) : 1 };
    });
    return { ...shape, outline, openings };
}
/** Take a corner out; the two walls either side become one. */
export function removeShapeVertex(shape, i) {
    const n = shape.outline.length;
    if (n <= 3)
        return shape;
    const outline = shape.outline.filter((_, k) => k !== i);
    const prev = (i - 1 + n) % n;
    // Both walls become the one from `prev`'s corner to the corner after `i`
    // (re-indexed below). An opening on either keeps its place in the room: it
    // is put where its own point falls on the merged wall, measured along that
    // wall — a door halfway along a 10 ft wall meeting a 90 ft one stays 5 ft
    // from the corner, not half of the merged wall's first half.
    const mergedIdx = prev > i ? prev - 1 : prev;
    const from = shape.outline[prev];
    const to = shape.outline[(i + 1) % n];
    const mergedBulge = from.bulge ?? 0;
    const mergedLen = wallLength(from, to, mergedBulge);
    const openings = shape.openings.map((o) => {
        if (o.segment === i || o.segment === prev) {
            const { a, b, bulge } = wallAt(shape.outline, o.segment);
            const at = pointOnWall(a, b, bulge, o.position).point;
            const t = projectOntoCurvedWall(from, to, mergedBulge, at);
            return {
                ...o,
                segment: mergedIdx,
                position: clampOpeningPosition(mergedLen, o.widthFt, t),
            };
        }
        return { ...o, segment: o.segment > i ? o.segment - 1 : o.segment };
    });
    return { ...shape, outline, openings };
}
/**
 * Make wall `i` span exactly `lengthFt` corner to corner by sliding its far
 * corner along the wall. The walls after it move with that corner, keeping
 * their direction; the closing wall takes up the difference. A curved wall
 * keeps its curve, so its length along the curve grows in proportion.
 */
export function setWallLength(shape, i, lengthFt) {
    const n = shape.outline.length;
    const { a, b } = wallAt(shape.outline, i);
    const current = Math.hypot(b.x - a.x, b.y - a.y);
    if (current < EPS || lengthFt <= 0)
        return shape;
    const scale = lengthFt / current;
    const dx = (b.x - a.x) * (scale - 1);
    const dy = (b.y - a.y) * (scale - 1);
    const end = (i + 1) % n;
    const outline = shape.outline.map((p, k) => {
        // Move the far corner and every later corner up to (not including) the first.
        const moves = end === 0 ? k === 0 : k >= end;
        return moves ? { ...p, x: round(p.x + dx), y: round(p.y + dy) } : p;
    });
    return { ...shape, outline };
}
/** How far from its old place an opening may land on a new wall and still be kept. */
const REMAP_TOLERANCE_FT = 1;
/**
 * Carry openings onto a new outline by where they are, not by wall number:
 * each keeps its place if a wall of the new outline runs through it in the
 * same direction, and is dropped otherwise. A preset, typed walls or a trace
 * numbers the walls afresh, so "wall 3" of the old room is no guide at all.
 */
export function remapOpenings(from, to, openings) {
    if (to.length < 2)
        return [];
    return openings.flatMap((o) => {
        if (o.segment < 0 || o.segment >= from.length)
            return [];
        const old = wallAt(from, o.segment);
        const at = pointOnWall(old.a, old.b, old.bulge, o.position);
        let best = null;
        for (let k = 0; k < to.length; k++) {
            const w = wallAt(to, k);
            const t = projectOntoCurvedWall(w.a, w.b, w.bulge, at.point);
            const on = pointOnWall(w.a, w.b, w.bulge, t);
            const dist = Math.hypot(on.point.x - at.point.x, on.point.y - at.point.y);
            // Same line either way round: a door on the far side of a thin wall is not it.
            const turn = Math.abs(Math.sin(on.angle - at.angle));
            if (dist <= REMAP_TOLERANCE_FT && turn < 0.35 && (!best || dist < best.dist)) {
                best = { segment: k, t, dist, reversed: Math.cos(on.angle - at.angle) < 0 };
            }
        }
        if (!best)
            return [];
        const w = wallAt(to, best.segment);
        const len = wallLength(w.a, w.b, w.bulge);
        if (o.widthFt > len + OPENING_FIT_FT)
            return [];
        // START and END are read along the wall's direction; a wall drawn the other
        // way round keeps the hinge on the same physical side by swapping them.
        const hinge = best.reversed && o.hinge ? (o.hinge === 'START' ? 'END' : 'START') : o.hinge;
        return [
            {
                ...o,
                segment: best.segment,
                ...(hinge ? { hinge } : {}),
                position: clampOpeningPosition(len, o.widthFt, round(best.t, 4)),
            },
        ];
    });
}
/** Curve (or straighten) a wall. */
export function setWallBulge(shape, i, bulge) {
    const outline = shape.outline.map((p, k) => {
        if (k !== i)
            return p;
        const rest = { x: p.x, y: p.y };
        return Math.abs(bulge) < 1e-3 ? rest : { ...rest, bulge: Math.max(-1, Math.min(1, bulge)) };
    });
    return { ...shape, outline };
}
export function moveShapeVertex(shape, i, to) {
    const outline = shape.outline.map((p, k) => k === i ? { ...p, x: round(to.x), y: round(to.y) } : p);
    return { ...shape, outline };
}
/** Where an opening sits: its two ends on the wall and the wall's direction. */
export function openingGeometry(shape, o) {
    const { a, b, bulge } = wallAt(shape.outline, o.segment);
    const len = wallLength(a, b, bulge) || 1;
    const half = Math.min(o.widthFt, len) / 2 / len;
    const t0 = Math.max(0, o.position - half);
    const t1 = Math.min(1, o.position + half);
    const start = pointOnWall(a, b, bulge, t0);
    const end = pointOnWall(a, b, bulge, t1);
    const centre = pointOnWall(a, b, bulge, o.position);
    return { start: start.point, end: end.point, centre: centre.point, angle: centre.angle };
}
// ─── Snapping while drawing ─────────────────────────────────────────
/**
 * Snap the next corner so the wall to it runs at a multiple of `stepDeg`
 * (90 keeps walls level or plumb, 45 allows diagonals), keeping its length.
 * Real halls are mostly right angles; a traced corner a few pixels off would
 * otherwise leave every wall slightly crooked.
 */
export function snapWallAngle(from, to, stepDeg = 90) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy);
    if (len < EPS)
        return { x: from.x, y: from.y };
    const step = (stepDeg * Math.PI) / 180;
    const angle = Math.round(Math.atan2(dy, dx) / step) * step;
    return { x: round(from.x + len * Math.cos(angle)), y: round(from.y + len * Math.sin(angle)) };
}
/** Round a point to a grid of `gridFt` feet (0 leaves it alone). */
export function snapToGridFt(p, gridFt) {
    if (gridFt <= 0)
        return p;
    return {
        x: round(Math.round(p.x / gridFt) * gridFt),
        y: round(Math.round(p.y / gridFt) * gridFt),
    };
}
/**
 * Stretch a shape to a new overall size — what typing a new width or depth
 * into the room form means for a room that has a drawn outline. Features move
 * with the floor under them but keep their own size.
 */
export function scaleRoomShape(shape, widthFt, heightFt) {
    const box = pointsBounds(flattenOutline(shape.outline));
    if (box.width < EPS || box.height < EPS)
        return shape;
    const sx = widthFt / box.width;
    const sy = heightFt / box.height;
    if (Math.abs(sx - 1) < EPS && Math.abs(sy - 1) < EPS)
        return shape;
    const at = (p) => ({
        x: round(box.minX + (p.x - box.minX) * sx),
        y: round(box.minY + (p.y - box.minY) * sy),
    });
    return {
        ...shape,
        outline: shape.outline.map((p) => ({ ...p, ...at(p) })),
        features: shape.features.map((f) => {
            const c = at({ x: f.x + f.widthFt / 2, y: f.y + f.heightFt / 2 });
            return { ...f, x: round(c.x - f.widthFt / 2), y: round(c.y - f.heightFt / 2) };
        }),
        // A traced drawing no longer lines up with a stretched outline.
        background: null,
    };
}
//# sourceMappingURL=roomShape.js.map